import React, { Fragment, useState, useEffect } from "react";
import PortalMenu from "../Common/PortalMenu";
import AppPagination from "../Common/AppPagination";
import { Row, Col, Card, Badge, Dropdown, Modal, Button, Table } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import SignatureModal from "../Common/SignatureModal";
import MomEmailModal from "../Common/MomEmailModal";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";

const MOMList = () => {
 const user = useSelector((state) => state.auth?.userDetails || {});
 const [moms, setMoms] = useState([]);
 const [selectedMOM, setSelectedMOM] = useState(null);
 const [showModal, setShowModal] = useState(false);
 const [loading, setLoading] = useState(false);
 const [previewingPdf, setPreviewingPdf] = useState(false);
 const [showSignModal, setShowSignModal] = useState(false);
 const [showEmailModal, setShowEmailModal] = useState(false);
 const [emailMom, setEmailMom] = useState(null);
 const [approveId, setApproveId] = useState(null);
 const location = useLocation();

 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [filteredWorkOrders, setFilteredWorkOrders] = useState([]);

 const [projectFilter, setProjectFilter] = useState("");
 const [workOrderFilter, setWorkOrderFilter] = useState("");

 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 limit: 10,
 hasNext: false,
 hasPrev: false,
 });

 useEffect(() => {
 fetchProjects();
 fetchWorkOrders();
 }, []);

 const fetchProjects = async () => {
 try {
 const res = await api.get("/projects?limit=1000");
 if (res.data.success) setProjects(res.data.data);
 } catch (err) {
 console.error("Failed to load projects:", err);
 }
 };

 const fetchWorkOrders = async () => {
 try {
 const res = await api.get("/work-orders?limit=1000");
 if (res.data.success) {
 setWorkOrders(res.data.data);
 setFilteredWorkOrders(res.data.data);
 }
 } catch (err) {
 console.error("Failed to load work orders:", err);
 }
 };

 useEffect(() => {
 if (workOrderFilter !== null) {
 fetchMOMs();
 }
 }, [pagination.currentPage, pagination.limit, workOrderFilter, projectFilter]);

 useEffect(() => {
 const params = new URLSearchParams(location.search);
 const wo = params.get("workOrder") || "";
 const proj = params.get("project") || "";

 if (proj) {
 setProjectFilter(proj);
 const filtered = workOrders.filter(
 (w) => String(w.project?._id || w.project) === String(proj)
      );
      setFilteredWorkOrders(filtered);
    } else {
      setFilteredWorkOrders(workOrders);
    }

 setWorkOrderFilter(wo);

 setPagination((prev) => ({
 ...prev,
 currentPage: 1,
 }));
 }, [location.search, workOrders]);

 // ================= FETCH =================
 const fetchMOMs = async () => {
 try {
 setLoading(true);

 let url = `/minutesofmeetings?page=${pagination.currentPage}&limit=${pagination.limit}&sortBy=createdAt&sortOrder=desc`;

 if (projectFilter) {
 url += `&project=${projectFilter}`;
 }
 if (workOrderFilter) {
 url += `&workOrder=${workOrderFilter}`;
 }

 const res = await api.get(url);

 if (res.data.success) {
 setMoms(res.data.data);

 if (res.data.pagination) {
 setPagination((prev) => ({
 ...prev,
 currentPage: res.data.pagination.currentPage,
 totalPages: res.data.pagination.totalPages,
 totalCount: res.data.pagination.totalCount,
 hasNext: res.data.pagination.hasNextPage,
 hasPrev: res.data.pagination.hasPrevPage,
 }));
 }
 }
 } catch (err) {
 swal("Error", "Failed to load MOMs", "error");
 } finally {
 setLoading(false);
 }
 };
 const handlePageChange = (page) => {
 setPagination(prev => ({ ...prev, currentPage: page }));
 };

 // ================= APPROVE =================
 const handleApprove = async (id) => {
 const confirm = await swal({
 title: "Approve MOM?",
 text: "Signature is required for approval.",
 icon: "info",
 buttons: ["Cancel", "Provide Signature"],
 });

 if (!confirm) return;

 setApproveId(id);
 setShowSignModal(true);
 };

 const handleSaveSignature = async (file) => {
 try {
 setLoading(true);

 const formData = new FormData();
 formData.append("clientSignature", file);

 const res = await api.patch(`/minutesofmeetings/${approveId}/approve`, formData, {
 headers: {
 "Content-Type": "multipart/form-data",
 },
 });

 if (res.data.success) {
 swal("Approved!", "MOM approved successfully", "success");
 setShowSignModal(false);
 setApproveId(null);
 fetchMOMs();
 }
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to approve MOM",
 "error",
 );
 } finally {
 setLoading(false);
 }
 };

 // ================= REJECT =================
 const handleReject = async (id) => {
 const remarks = prompt("Enter rejection reason:");
 if (!remarks) return;

 try {
 const res = await api.patch(`/minutesofmeetings/${id}/reject`, {
 remarks,
 });

 if (res.data.success) {
 swal("Rejected!", "MOM rejected", "success");
 fetchMOMs();
 }
 } catch {
 swal("Error", "Failed to reject MOM", "error");
 }
 };

 // ================= PDF PREVIEW =================
 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);

 const res = await api.get(`/minutesofmeetings/${id}/pdf`, {
 responseType: "blob",
 });

 const fileURL = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" }),
 );

 window.open(fileURL, "_blank");
 } catch {
 swal("Error", "Failed to preview PDF", "error");
 } finally {
 setPreviewingPdf(false);
 }
 };

 // ================= PDF DOWNLOAD =================
 const handleDownloadPDF = async (id) => {
 try {
 setPreviewingPdf(true);

 const res = await api.get(`/minutesofmeetings/${id}/pdf`, {
 responseType: "blob",
 });

 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.download = `MOM-${id}.pdf`;
 link.click();
 } catch {
 swal("Error", "Failed to download PDF", "error");
 } finally {
 setPreviewingPdf(false);
 }
 };

 const handleOpenEmail = (mom) => {
 setEmailMom(mom);
 setShowEmailModal(true);
 };

 const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Are you sure?",
 text: "This MOM will be deleted permanently!",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 const res = await api.delete(`/minutesofmeetings/${id}`);
 if (res.data.success) {
 swal("Deleted!", "MOM deleted successfully", "success");
 fetchMOMs();
 }
 } catch {
 swal("Error", "Failed to delete MOM", "error");
 }
 };

 const handleView = async (id) => {
 try {
 const res = await api.get(`/minutesofmeetings/${id}`);
 if (res.data.success) {
 setSelectedMOM(res.data.data);
 setShowModal(true);
 }
 } catch {
 swal("Error", "Failed to load MOM details", "error");
 }
 };

 const formatDate = (date) => {
 if (!date) return "---";
 return new Date(date).toLocaleDateString("en-IN");
 };

 const getStatusBadge = (status) => {
 switch (status) {
 case "Approved":
 return "success";
 case "Pending":
 return "warning";
 case "Rejected":
 return "danger";
 default:
 return "secondary";
 }
 };

 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <circle fill="#000" cx="5" cy="12" r="2" />
 <circle fill="#000" cx="12" cy="12" r="2" />
 <circle fill="#000" cx="19" cy="12" r="2" />
 </svg>
 );

   const handleProjectFilterChange = (e) => {
    const val = e.target.value;
    setProjectFilter(val);
    setWorkOrderFilter("");

    if (val) {
      const filtered = workOrders.filter(
        (wo) => String(wo.project?._id || wo.project) === String(val)
      );
      setFilteredWorkOrders(filtered);
    } else {
      setFilteredWorkOrders(workOrders);
    }

    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleWorkOrderFilterChange = (e) => {
    const val = e.target.value;
    setWorkOrderFilter(val);
    if (val) {
      const selectedWo = workOrders.find((w) => String(w._id) === String(val));
      const autoProjId = selectedWo?.project?._id || selectedWo?.project;
      if (autoProjId) {
        setProjectFilter(String(autoProjId));
        const filtered = workOrders.filter(
          (wo) => String(wo.project?._id || wo.project) === String(autoProjId)
        );
        setFilteredWorkOrders(filtered);
      }
    }
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

 return (
 <Fragment>
 <PageTitle activeMenu="MOM" motherMenu="Projects" />

 <Row className="mb-3">
 <Col lg={12}>
 <Card>
 <Card.Body>
 <Row>
 <Col md={4}>
 <label className="form-label">Project</label>
 <select
                    className="form-select form-select-sm"
                    value={workOrderFilter}
                    onChange={handleWorkOrderFilterChange}
                  >
 <option value="">All Work Orders</option>
 {filteredWorkOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}{wo.woNumber ? ` - ${wo.woNumber}` : ""}
 </option>
 ))}
 </select>
 </Col>

 <Col md={4} className="d-flex align-items-end">
 <Button
 variant="outline-secondary"
 className="w-100 btn-sm"
 onClick={() => {
 setProjectFilter("");
 setWorkOrderFilter("");
 setFilteredWorkOrders(workOrders);
 setPagination((prev) => ({ ...prev, currentPage: 1 }));
 }}
 >
 Reset Filters
 </Button>
 </Col>
 </Row>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>MOM List</Card.Title>
 <PermissionWrapper user={user} required={["create:moms"]}>
 <Link
 to={
 workOrderFilter
 ? `/mom/add?workOrder=${workOrderFilter}`
 : "/mom/add"
 }
 className="btn btn-primary"
 >
 + Add MOM
 </Link>
 </PermissionWrapper>
 </Card.Header>

 <Card.Body>
 {loading ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover>
 <thead>
 <tr>
 <th>#</th>
 <th>MOM No</th>
 <th>Date</th>
 <th>Project</th>
 <th>Work Order</th>
 <th>Prepared By</th>
 <th>Status</th>
 <th>Actions</th>
 </tr>
 </thead>

 <tbody>
 {moms.map((mom, i) => (
 <tr key={mom._id}>
 <td>
 {(pagination.currentPage - 1) * pagination.limit +
 (i + 1)}
 </td>
 <td>{mom.momNumber || "—"}</td>
 <td>{formatDate(mom.meetingDate)}</td>
 <td>{mom.project?.projectName || "—"}</td>
 <td>{mom.workOrder?.woNo || mom.workOrder?.woNumber || "—"}</td>
 <td>{mom.preparedBy}</td>
 <td>
 <Badge bg={getStatusBadge(mom.status)}>
 {mom.status}
 </Badge>
 </td>
 <td>
 <Dropdown>
 <Dropdown.Toggle
 variant=""
 as="div"
 className="btn i-false"
 >
 {svgMenu}
 </Dropdown.Toggle>

 <Dropdown.Menu align="end" as={PortalMenu}>
 <PermissionWrapper user={user} required={["read:moms"]}>
 <Dropdown.Item
 onClick={() => handleView(mom._id)}
 >
 <i className="fas fa-eye me-2"></i>View
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["edit:moms"]}>
 <Dropdown.Item
 as={Link}
 to={
 workOrderFilter
 ? `/mom/edit/${mom._id}?workOrder=${workOrderFilter}`
 : `/mom/edit/${mom._id}`
 }
 >
 <i className="fas fa-edit me-2"></i>Edit
 </Dropdown.Item>
 </PermissionWrapper>

 <Dropdown.Divider />

 {mom.status === "Pending" && (
 <>
 <PermissionWrapper user={user} required={["approve:moms"]}>
 <Dropdown.Item
 className="text-success"
 onClick={() => handleApprove(mom._id)}
 >
 <i className="fas fa-check me-2"></i>
 Approve
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["reject:moms"]}>
 <Dropdown.Item
 className="text-danger"
 onClick={() => handleReject(mom._id)}
 >
 <i className="fas fa-times me-2"></i>
 Reject
 </Dropdown.Item>
 </PermissionWrapper>

 <Dropdown.Divider />
 </>
 )}

 <PermissionWrapper user={user} required={["read:moms"]}>
 <Dropdown.Item
 onClick={() => handlePreviewPDF(mom._id)}
 >
 <i className="fas fa-eye me-2 text-primary"></i>
 Preview PDF
 </Dropdown.Item>

 <Dropdown.Item
 onClick={() => handleDownloadPDF(mom._id)}
 >
 <i className="fas fa-file-pdf me-2 text-danger"></i>
 Download PDF
 </Dropdown.Item>

 <Dropdown.Item
 onClick={() => handleOpenEmail(mom)}
 >
 <i className="fas fa-envelope me-2 text-info"></i>
 Send Mail
 </Dropdown.Item>
 </PermissionWrapper>

 <Dropdown.Divider />

 <PermissionWrapper user={user} required={["delete:moms"]}>
 <Dropdown.Item
 className="text-danger"
 onClick={() => handleDelete(mom._id)}
 >
 <i className="fas fa-trash me-2"></i>
 Delete
 </Dropdown.Item>
 </PermissionWrapper>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 {/* Pagination */}
 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={moms.length}
 limit={pagination.limit}
 onPageChange={handlePageChange}
 />
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* ================= MODAL ================= */}
 <Modal
 show={showModal}
 onHide={() => setShowModal(false)}
 centered
 size="lg"
 >
 <Modal.Header closeButton>
 <Modal.Title>MOM Details</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {selectedMOM && (
 <>
 <p>
 <strong>Date:</strong> {formatDate(selectedMOM.meetingDate)}
 </p>
 <p>
 <strong>Project:</strong> {selectedMOM.project?.projectName}
 </p>
 <p>
 <strong>Work Order:</strong> {selectedMOM.workOrder?.woNo}
 </p>
 <p>
 <strong>Prepared By:</strong> {selectedMOM.preparedBy}
 </p>
 <p>
 <strong>Venue:</strong> {selectedMOM.venue}
 </p>

 <hr />

 <h6>Minutes</h6>
 <ul>
 {selectedMOM.minutes?.map((m, i) => (
 <li key={i}>{m}</li>
 ))}
 </ul>
 </>
 )}
 </Modal.Body>

 <Modal.Footer>
 {selectedMOM && (
 <>
 <Button
 variant="outline-primary"
 onClick={() => handlePreviewPDF(selectedMOM._id)}
 >
 Preview PDF
 </Button>

 <Button
 variant="danger"
 onClick={() => handleDownloadPDF(selectedMOM._id)}
 >
 Download PDF
 </Button>
 </>
 )}

 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>

 {/* ================= FULLSCREEN PDF LOADER ================= */}
 {previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing MOM PDF…</h6>
 </div>
 </div>
 )}

 <SignatureModal
 show={showSignModal}
 onHide={() => {
 setShowSignModal(false);
 setApproveId(null);
 }}
 onSave={handleSaveSignature}
 title="Client Signature for MOM Approval"
 />

 <MomEmailModal
 show={showEmailModal}
 onHide={() => {
   setShowEmailModal(false);
   setEmailMom(null);
 }}
 mom={emailMom}
 type="mom"
 />
 </Fragment>
 );
};

export default MOMList;
